import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import StorageService from '../services/StorageService';

// Define the shape of our cache context with methods for getting, setting and clearing data
interface CacheContextType {
  getData: {
    <T>(key: string): T | null;
  };
  setData: (key: string, value: any, ttl?: number) => void;
  clearData: (key: string) => void;
}

// Create a React context to share cache functionality throughout the app
const CacheContext = createContext<CacheContextType | null>(null);

// Provider component that wraps the app and provides cache functionality to children
export function CacheProvider({ children }: { children: ReactNode }) {
  // Get data from cache storage using the provided key
  const getData = <T,>(key: string) => StorageService.getCache<T>(key);
  
  // Store data in cache with optional expiry time
  const setData = <T,>(key: string, data: T, expiry?: number) => 
    StorageService.setCache(key, data, expiry);
  
  // Clear either a specific cache entry or all cache data
  const clearData = (key?: string) => StorageService.clearCache(key);

  return (
    <CacheContext.Provider value={{ getData, setData, clearData }}>
      {children}
    </CacheContext.Provider>
  );
}

// Custom hook to easily access cache functionality in components
export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
}; 